import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Save } from "lucide-react";
import { toast } from "sonner";

const MATS = [
  "grey_cement", "white_cement", "sand", "grit_10mm", "grit_20mm",
  "marble_dust", "marble_chips_5mm", "marble_chips_10mm", "marble_pieces",
  "glass_chips", "glass_powder",
  "red_color", "black_color", "yellow_color", "chocolate_color", "green_color", "blue_color",
  "ar_glass_fiber", "hardner", "water",
];
const UNIT_HINT = { grey_cement: "bags", white_cement: "bags", sand: "kg", water: "liters" };
const CATEGORIES = ["pavers", "chequered_tiles", "terrazzo_tiles", "countertops", "wash_basins"];

const prettyMat = (m) => (m || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Costs() {
  const [rates, setRates] = useState({});
  const [fuel, setFuel] = useState(95);
  const [labor, setLabor] = useState(60);
  const [prices, setPrices] = useState({});

  useEffect(() => {
    api.get("/costs/settings").then(({ data }) => {
      setRates(data.material_rates || {});
      setFuel(data.genset_fuel_rate ?? 95);
      setLabor(data.labor_rate_per_hour ?? 60);
      setPrices(data.product_prices || {});
    }).catch(() => {});
  }, []);

  const setRate = (mat, field, val) => setRates((r) => ({ ...r, [mat]: { ...(r[mat] || {}), [field]: field === "rate" ? (val === "" ? "" : parseFloat(val)) : val } }));
  const setPrice = (cat, val) => setPrices((p) => ({ ...p, [cat]: val === "" ? "" : parseFloat(val) }));

  const save = async () => {
    try {
      await api.put("/costs/settings", {
        material_rates: rates,
        genset_fuel_rate: parseFloat(fuel) || 0,
        labor_rate_per_hour: parseFloat(labor) || 0,
        product_prices: prices,
      });
      toast.success("Cost settings saved");
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Admin only</div>
            <h1 className="font-display text-4xl tracking-tighter font-black">Costs & Pricing</h1>
            <p className="text-[#6E6E6A] text-sm mt-2">These rates power the feasibility & profit reports.</p>
          </div>
          <Button onClick={save} data-testid="costs-save" className="bg-[#1C1C1A] hover:bg-black text-white gap-2"><Save className="w-4 h-4" /> Save all</Button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <Card className="border border-[#D8D8D5] shadow-none rounded-md p-6">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A] mb-4">Operational rates</div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Diesel / genset fuel (₹ per litre)</Label>
                <Input type="number" step="0.5" value={fuel} onChange={(e) => setFuel(e.target.value)} data-testid="fuel-rate" />
              </div>
              <div>
                <Label className="text-xs">Labor (₹ per hour)</Label>
                <Input type="number" step="1" value={labor} onChange={(e) => setLabor(e.target.value)} data-testid="labor-rate" />
              </div>
            </div>

            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A] mt-8 mb-3">Product default selling prices</div>
            <div className="space-y-2">
              {CATEGORIES.map((c) => (
                <div key={c} className="flex items-center gap-3">
                  <div className="w-32 text-sm capitalize">{c.replace(/_/g," ")}</div>
                  <Input type="number" step="1" value={prices[c] ?? ""} placeholder="₹/unit" onChange={(e) => setPrice(c, e.target.value)} className="flex-1" />
                </div>
              ))}
            </div>
          </Card>

          <Card className="border border-[#D8D8D5] shadow-none rounded-md p-0 overflow-hidden">
            <div className="p-5 border-b border-[#D8D8D5]">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Material purchase rates</div>
              <p className="text-xs text-[#6E6E6A] mt-1">Set rate per unit — used to cost each consumption entry.</p>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Material</TableHead><TableHead>Rate (₹)</TableHead><TableHead>Unit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {MATS.map((m) => (
                    <TableRow key={m}>
                      <TableCell className="text-sm">{prettyMat(m)}</TableCell>
                      <TableCell>
                        <Input type="number" step="0.5" value={rates[m]?.rate ?? ""} placeholder="0"
                          onChange={(e) => setRate(m, "rate", e.target.value)}
                          className="h-8 w-24" />
                      </TableCell>
                      <TableCell>
                        <Input value={rates[m]?.unit ?? UNIT_HINT[m] ?? "kg"}
                          onChange={(e) => setRate(m, "unit", e.target.value)}
                          className="h-8 w-20" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
